import React, { useState } from 'react';
import { Copy, ExternalLink, MessageCircle } from 'lucide-react';

interface GPTRedirectionProps {
  platform: string;
  accountIds: number[];
  campaignIds: number[];
  dateRange: { start: string; end: string };
  accounts: Array<{ id: number; name: string }>;
  campaigns: Array<{ id: number; name: string }>;
  isDisabled?: boolean;
}

const GPTRedirection: React.FC<GPTRedirectionProps> = ({
  platform,
  accountIds,
  campaignIds,
  dateRange,
  accounts,
  campaigns,
  isDisabled = false,
}) => {
  const [copied, setCopied] = useState(false);

  const formatSelectionCriteria = () => {
    const selectedAccounts = accounts.filter(account => accountIds.includes(account.id));
    const selectedCampaigns = campaigns.filter(campaign => campaignIds.includes(campaign.id));

    return `Campaign Analysis Request: Please analyze the campaign performance data for these criteria.

Date Range: ${dateRange.start} to ${dateRange.end}

Platform: ${platform}

Accounts:
${selectedAccounts.sort((a, b) => a.name.localeCompare(b.name)).map(account => `• ${account.name}`).join('\n')}

Campaigns:
${selectedCampaigns.sort((a, b) => a.name.localeCompare(b.name)).map(campaign => `• ${campaign.name}`).join('\n')}`;
  };

  const handleCopySelectionCriteria = async() => {
    try {
      await navigator.clipboard.writeText(formatSelectionCriteria());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy selection criteria:', error);
    }
  };

  const handleOpenChatGPT = () => {
    window.open('https://chatgpt.com/g/g-6859b4107fa081918b3f20b8ab34884c-lumio-v0-1', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex flex-col w-full rounded-2xl bg-white border border-gray-400">
      <div className="p-8">
        <div className="text-2xl font-light mb-4">
          AI Reporter
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-0">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full items-center justify-center">
              <MessageCircle className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-gray-600 mb-4 leading-relaxed">
                Analyze your campaign data with our specialized AI assistant.
                {' '}
                <br className="hidden sm:block" />
                Get detailed reports, performance summaries, and actionable insights tailored to your advertising data.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleCopySelectionCriteria}
                  disabled={isDisabled}
                  className={`inline-flex items-center px-6 py-3 font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 w-full sm:min-w-[250px] sm:w-auto ${
                    isDisabled
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                  title={isDisabled ? 'Complete your selection criteria first' : 'Copy your current selection to share with AI'}
                >
                  <Copy className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="text-left">
                    {copied ? 'Copied!' : 'Copy Current Selection'}
                  </span>
                </button>

                <button
                  onClick={handleOpenChatGPT}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full sm:w-auto"
                >
                  <ExternalLink className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="text-left">
                    Open AI Assistant
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GPTRedirection;